import {
    ApolloClient,
    HttpLink,
    InMemoryCache,
    Observable,
    from,
    fromPromise,
    split,
    type FetchResult,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { useAuthStore } from "../stores/authStore";
import { useAdminStore } from "../stores/adminStore";
import { endUserSession } from "./session";
import { GRAPHQL_URL } from "../constants";

// Use centralized GraphQL URL from constants
const graphqlUrl = GRAPHQL_URL;
// One navigation fires a dozen distinct queries. The API limiter counts HTTP
// requests, so collapse the ones that start together into a single POST. The
// server refuses batches over 20 or containing a mutation, so mutations go
// alone and every login attempt still costs one request.
//
// A batch answers when its slowest query does. A heavy aggregation passes
// `context: { batch: false }` so it cannot hold the page's light queries back.
const batchedQueryLink = new BatchHttpLink({
    uri: graphqlUrl,
    batchMax: 20,
    batchInterval: 10,
});
const singleOperationLink = new HttpLink({ uri: graphqlUrl });

const operationKind = (query: { definitions: readonly any[] }) =>
    query.definitions.find((def) => def.kind === "OperationDefinition")?.operation;

const httpLink = split(
    (operation) => operationKind(operation.query) === "query" && operation.getContext().batch !== false,
    batchedQueryLink,
    singleOperationLink,
);

// The store first, then localStorage: AdminLogin.tsx writes there directly, and
// a tab that reloaded before the store rehydrated only has it there.
const adminToken = () => {
    const stored = (() => {
        try { return useAdminStore.getState().token as string | null; } catch { return null; }
    })();
    return stored || localStorage.getItem('adminToken');
};

const onAdminRoute = () => window.location.pathname.startsWith('/hq');

/**
 * The token this request should carry, chosen by route rather than by a fixed
 * precedence.
 *
 * An admin token is not a user token: the backend's user authenticator refuses
 * it outright, so a leftover `adminToken` from an /hq session fails every
 * customer query with "You must be logged in". It wins on /hq, where the admin
 * resolvers are the only audience, and stands in off /hq only when there is no
 * customer session at all.
 */
const requestToken = () => {
    const userToken = useAuthStore.getState().token;
    return onAdminRoute() ? adminToken() || userToken : userToken || adminToken();
};

const authLink = setContext((_, { headers }) => {
    const token = requestToken();

    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : "",
        },
    };
});

const isAuthFailure = (message: string | undefined, code: unknown) =>
    code === 'UNAUTHENTICATED' ||
    code === 'AuthenticationError' ||
    !!message?.toLowerCase().includes('not authenticated') ||
    !!message?.toLowerCase().includes('authentication') ||
    !!message?.toLowerCase().includes('unauthorized');

const resultFailedAuth = (result: FetchResult | null | undefined) =>
    !!result?.errors?.some(({ message, extensions }) => isAuthFailure(message, extensions?.code));

// Mirrors `requestToken`: whichever session supplied the rejected token is the
// one that has to end. A stale `adminToken` left in storage must not make a
// customer's expired session look like an operator's.
const isAdminSession = () => onAdminRoute() || (!useAuthStore.getState().token && !!adminToken());

const endAdminSession = () => {
    useAdminStore.getState().logout();
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    if (!window.location.pathname.startsWith('/hq/login')) {
        window.location.href = '/hq/login';
    }
};

// One refresh at a time. Opening a dashboard tab fires a dozen queries at
// once, and an aged-out access token fails all of them — without this they
// would each spend the single-use refresh token and all but one would fail.
let adminRefreshInFlight: Promise<boolean> | null = null;
const refreshAdminSession = () => {
    if (!adminRefreshInFlight) {
        adminRefreshInFlight = useAdminStore
            .getState()
            .refreshAdminToken()
            .finally(() => {
                adminRefreshInFlight = null;
            });
    }
    return adminRefreshInFlight;
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    graphQLErrors?.forEach(({ message, locations, path }) => {
        console.error(
            `GraphQL error on ${operation.operationName}: Message: ${message}, Location: ${locations}, Path: ${path}`
        );
    });
    if (networkError) {
        console.error(`Network error on ${operation.operationName}:`, networkError);
    }

    const authFailed =
        graphQLErrors?.some(({ message, extensions }) => isAuthFailure(message, extensions?.code)) ||
        (!!networkError && 'statusCode' in networkError && networkError.statusCode === 401);

    if (!authFailed) return;

    if (!isAdminSession()) {
        endUserSession();
        return;
    }

    // The admin access token lives 24h against a 7d refresh token, so the
    // overwhelming cause of this is an access token that aged out mid-session
    // — recoverable without sending the operator back to the login page. Only
    // a refresh that actually fails means the session is over.
    if (operation.getContext().adminTokenRefreshed) {
        endAdminSession();
        return;
    }
    operation.setContext({ adminTokenRefreshed: true });

    return fromPromise(refreshAdminSession()).flatMap((refreshed) => {
        if (!refreshed) {
            endAdminSession();
            return new Observable<never>((observer) => {
                observer.error(new Error('Your admin session expired. Please sign in again.'));
            });
        }
        // Apollo hands a retried operation's result straight to the caller
        // without passing it back through this handler, so a retry that fails
        // auth a second time is the end of the line: nothing else will notice
        // it, and the component renders the raw "You must be logged in".
        return forward(operation).map((result) => {
            if (resultFailedAuth(result)) endAdminSession();
            return result;
        });
    });
});

// WS link for subscriptions
const wsUrl = (graphqlUrl || "").replace(/^http/, "ws");
const wsLink = new GraphQLWsLink(createClient({ 
    url: wsUrl, 
    connectionParams: () => {
        const token = requestToken();
        return { authorization: token ? `Bearer ${token}` : "" };
    },
    // Reconnection configuration
    shouldRetry: (errOrCloseEvent: unknown) => {
        // Retry on connection errors and socket closures
        if (errOrCloseEvent instanceof Error) {
            // Retry on network errors, but not on protocol/auth errors
            return errOrCloseEvent.message?.includes('Socket closed') || 
                   errOrCloseEvent.message?.includes('WebSocket') ||
                   errOrCloseEvent.message?.includes('connection') ||
                   false;
        }
        // Retry on close events (code 1000 is normal closure, others might be transient)
        if (typeof errOrCloseEvent === 'object' && errOrCloseEvent !== null && 'code' in errOrCloseEvent) {
            return (errOrCloseEvent as { code: number }).code !== 1000;
        }
        return true; // Default to retry if we can't determine
    },
    retryAttempts: 5, // Maximum number of retry attempts
    retryWait: async (retries: number) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s (capped at 16s)
        const delay = Math.min(1000 * Math.pow(2, retries), 16000);
        await new Promise(resolve => setTimeout(resolve, delay));
    },
    // Keep alive configuration
    keepAlive: 30000, // Send ping every 30 seconds
    on: {
        connected: () => {
        },
        closed: () => {
        },
        error: (error) => {
            console.error('❌ GraphQL WebSocket error:', error);
        },
    },
}));

// Route subscription operations to WS, others to HTTP
const splitLink = split(
    ({ query }) => operationKind(query) === 'subscription',
    wsLink,
    authLink.concat(httpLink)
);

export const apolloClient = new ApolloClient({
    link: from([errorLink, splitLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            errorPolicy: "all",
        },
        query: {
            errorPolicy: "all",
        },
    },
});
